import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeaders, removeAuthToken, getCurrentUser } from '@/App';
import { 
  Link2, Users, LinkIcon, BarChart3, LogOut, Plus, Edit, Trash2, X,
  MoreVertical, Eye, EyeOff, Globe, MapPin, Clock, RotateCcw, ExternalLink, Shield, MessageCircle, Copy
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DateRangePicker from '@/components/DateRangePicker';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [links, setLinks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showUserModal, setShowUserModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [selectedLinkForStats, setSelectedLinkForStats] = useState(null);
  const [linkAnalytics, setLinkAnalytics] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownOpenAbove, setDropdownOpenAbove] = useState(false);
  const [selectedDays, setSelectedDays] = useState(14);
  const [allUsers, setAllUsers] = useState([]);
  
  // Admin kullanıcı bilgisi
  const currentUser = getCurrentUser();
  const adminUserId = currentUser?.sub || currentUser?.user_id || currentUser?.id || '';
  
  const [newUser, setNewUser] = useState({ email: '', username: '', password: '', role: 'user' });
  const [newLink, setNewLink] = useState({ short_code: '', destination_url: '', title: '', owner_user_id: '' });
  const [adminLinks, setAdminLinks] = useState([]); // Admin'in kendi linkleri

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      setAllUsers(res.data.filter(u => u.role !== 'admin'));
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  }, []);

  // Admin'in kendi linklerini getir
  const fetchAdminLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/links`, { headers: getAuthHeaders() });
      // Sadece admin'e ait linkleri filtrele
      const myLinks = res.data.filter(link => link.user_id === adminUserId);
      setAdminLinks(myLinks);
    } catch (error) {
      console.error('Admin linkleri yüklenemedi');
    }
  }, [adminUserId]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (activeTab === 'overview') {
        const res = await axios.get(`${API}/analytics/global?days=${selectedDays}`, { headers });
        setAnalytics(res.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/admin/users`, { headers });
        setUsers(res.data);
      } else if (activeTab === 'links') {
        const res = await axios.get(`${API}/admin/links`, { headers });
        setLinks(res.data);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Veri yüklenemedi');
      if (error.response?.status === 401) {
        removeAuthToken();
        navigate('/admin/login');
      }
    }
  }, [activeTab, selectedDays, navigate]);

  useEffect(() => {
    fetchData();
    fetchAllUsers();
    fetchAdminLinks();
  }, [fetchData, fetchAllUsers, fetchAdminLinks]);

  const fetchLinkAnalytics = async (linkId) => {
    try {
      const res = await axios.get(`${API}/analytics/link/${linkId}?days=${selectedDays}`, { headers: getAuthHeaders() });
      setLinkAnalytics(res.data);
    } catch (error) {
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    navigate('/admin/login');
  };

  // Link kopyalama fonksiyonu
  const copyLink = (shortCode) => {
    navigator.clipboard.writeText(`${window.location.origin}/${shortCode}`);
    toast.success('Link kopyalandı!');
  };

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers: getAuthHeaders() });
      toast.success('Kullanıcı oluşturuldu');
      setShowUserModal(false);
      setNewUser({ email: '', username: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const updateData = { ...newUser };
      if (!updateData.password) delete updateData.password;
      await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { headers: getAuthHeaders() });
      toast.success('Kullanıcı güncellendi');
      setShowUserModal(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Kullanıcı silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Kullanıcı onaylandı');
      fetchData();
    } catch (error) {
      toast.error('Onaylama başarısız');
    }
  };

  const handleCreateLink = async () => {
    try {
      await axios.post(`${API}/admin/links`, newLink, { headers: getAuthHeaders() });
      toast.success('Link oluşturuldu');
      setShowLinkModal(false);
      setNewLink({ short_code: '', destination_url: '', title: '', owner_user_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleUpdateLink = async () => {
    try {
      await axios.put(`${API}/admin/links/${editingLink.id}`, newLink, { headers: getAuthHeaders() });
      toast.success('Link güncellendi');
      setShowLinkModal(false);
      setEditingLink(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Bu linki silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/links/${linkId}`, { headers: getAuthHeaders() });
      toast.success('Link silindi');
      setOpenDropdown(null);
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleLinkStatus = async (linkId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/links/${linkId}`, { is_active: !currentStatus }, { headers: getAuthHeaders() });
      toast.success(currentStatus ? 'Link devre dışı bırakıldı' : 'Link aktif edildi');
      setOpenDropdown(null);
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleViewStats = (link) => {
    setSelectedLinkForStats(link);
    fetchLinkAnalytics(link.id);
    setShowStatsModal(true);
    setOpenDropdown(null);
  };

  const handleResetStats = async (linkId) => {
    if (!window.confirm('Bu linkin tüm istatistiklerini sıfırlamak istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/links/${linkId}/stats`, { headers: getAuthHeaders() });
      toast.success('İstatistikler sıfırlandı');
      setOpenDropdown(null);
      fetchData();
    } catch (error) {
      toast.error('İstatistikler sıfırlanamadı');
    }
  };

  const openEditLinkModal = (link) => {
    setEditingLink(link);
    // Eğer link admin'e ait ise veya user_id yoksa, adminUserId kullan
    const linkOwnerId = link.user_id || adminUserId;
    setNewLink({
      short_code: link.short_code,
      destination_url: link.destination_url,
      title: link.title || '',
      owner_user_id: linkOwnerId
    });
    setShowLinkModal(true);
    setOpenDropdown(null);
  };

  // URL kısaltma fonksiyonu
  const truncateUrl = (url, maxLen = 25) => {
    if (!url) return '-';
    if (url.length <= maxLen) return url;
    return url.substring(0, maxLen) + '...';
  };

  // Referrer verilerini hazırla
  const prepareReferrerData = useMemo(() => {
    if (!analytics?.referrers) return [];
    return Object.entries(analytics.referrers)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value], idx) => ({ 
        name: name.length > 20 ? name.substring(0, 20) + '...' : name, 
        fullName: name,
        value,
        color: ['#FFD700', '#FF8C00', '#FF4500', '#FF1493', '#9370DB'][idx] || '#666'
      }));
  }, [analytics?.referrers]);

  // WhatsApp Modal State
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  // İstatistik Kartları Komponenti
  const StatsContent = ({ data }) => {
    // Link-özel referrer verilerini hazırla
    const linkReferrerData = useMemo(() => {
      if (!data?.referrers) return [];
      return Object.entries(data.referrers)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, value], idx) => ({ 
          name: name.length > 20 ? name.substring(0, 20) + '...' : name, 
          fullName: name,
          value,
          color: ['#FFD700', '#FF8C00', '#FF4500', '#FF1493', '#9370DB'][idx] || '#666'
        }));
    }, [data?.referrers]);
    
    return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* Normal kartlar */}
        {[
          { label: 'Toplam Tıklama', value: data.total_clicks, icon: BarChart3, color: 'text-purple-500' },
          { label: 'Benzersiz', value: data.unique_clicks, icon: Users, color: 'text-purple-500' }
        ].map((stat, idx) => (
          <div key={idx} className="stat-led-card-purple p-3 md:p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-400">{stat.label}</div>
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
            </div>
            <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
          </div>
        ))}
        
        {/* WhatsApp Kartı - Tıklanabilir */}
        <div 
          onClick={() => setShowWhatsAppModal(true)}
          className="stat-led-card-purple p-3 md:p-4 bg-green-500/10 border-green-500/50 cursor-pointer hover:bg-green-500/20 transition-all"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-400">WhatsApp</div>
            <img src="/whatsapp-logo.png" alt="WhatsApp" className="w-5 h-5 rounded" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-green-400">{data.today_whatsapp || 0}</div>
          <div className="text-[10px] text-gray-500 mt-1">Bugün</div>
        </div>
        
        {/* Diğer kartlar */}
        {[
          { label: 'Ülke', value: Object.keys(data.countries || {}).length, icon: Globe, color: 'text-purple-500' },
          { label: 'Şehir', value: Object.keys(data.cities || {}).length, icon: MapPin, color: 'text-purple-500' }
        ].map((stat, idx) => (
          <div key={idx + 3} className="stat-led-card-purple p-3 md:p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-400">{stat.label}</div>
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
            </div>
            <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Grafik - Modern Area Chart - TURUNCU TEMA */}
      <div className="bg-[#0a0a0a] rounded-xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base md:text-lg font-bold text-white">Günlük Tıklamalar</h3>
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-gray-400">Tıklama</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-400">WhatsApp</span>
            </div>
          </div>
        </div>
        <div className="h-[280px] sm:h-[300px] md:h-[320px] lg:h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily_clicks || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#ea580c" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#c2410c" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorWhatsApp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
              <XAxis 
                dataKey="date" 
                stroke="#888" 
                style={{ fontSize: '10px' }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}/${date.getMonth() + 1}`;
                }}
                tick={{ fill: '#888' }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={50}
              />
              <YAxis stroke="#888" style={{ fontSize: '10px' }} tick={{ fill: '#888' }} />
              <Tooltip 
                contentStyle={{ 
                  background: '#1a1a1a', 
                  border: '1px solid #8b5cf6', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  color: '#fff'
                }}
                formatter={(value, name) => [value, name === 'clicks' ? 'Tıklama' : 'WhatsApp']}
                labelFormatter={(label) => `Tarih: ${label}`}
              />
              <Area 
                type="monotone" 
                dataKey="clicks" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorClicks)" 
                name="clicks"
                dot={{ r: 5, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 7, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
              />
              <Area 
                type="monotone" 
                dataKey="whatsapp" 
                stroke="#22c55e" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorWhatsApp)" 
                name="whatsapp"
                dot={{ r: 4, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Trafik Kaynakları */}
      {linkReferrerData.length > 0 && (
      <div className="bg-[#0a0a0a] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3">Trafik Kaynakları</h3>
        <div className="space-y-3">
          {linkReferrerData.map((item, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className="bg-black text-white px-2 py-1 rounded border border-purple-500 truncate max-w-[200px]" title={item.fullName}>
                  {item.name}
                </span>
                <span className="text-gray-400 ml-2">{item.value}</span>
              </div>
              <div className="h-4 bg-white/5 rounded overflow-hidden">
                <div
                  className="h-full flex items-center px-2 text-white text-[10px] font-bold"
                  style={{
                    width: `${Math.max((item.value / (linkReferrerData[0]?.value || 1)) * 100, 5)}%`,
                    background: item.color
                  }}
                >
                  {Math.round((item.value / (linkReferrerData[0]?.value || 1)) * 100)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Son Tıklamalar - Tablo Görünümü */}
      <div className="bg-[#0a0a0a] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-500" />
          Son Tıklamalar
        </h3>
        <div className="overflow-x-auto -mx-4 px-4 max-h-96 overflow-y-auto">
          <table className="w-full text-xs min-w-[800px]">
            <thead className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
              <tr className="text-gray-400">
                <th className="text-left p-2 whitespace-nowrap">IP</th>
                <th className="text-left p-2 whitespace-nowrap">Ülke</th>
                <th className="text-left p-2 whitespace-nowrap">Şehir</th>
                <th className="text-left p-2 whitespace-nowrap">Kaynak</th>
                <th className="text-right p-2 whitespace-nowrap min-w-[120px]">Tarih / Saat</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_clicks?.length > 0 ? data.recent_clicks.map((click, idx) => {
                const countryCode = click.country_code || getCountryCode(click.country);
                
                return (
                  <tr key={idx} className="border-b border-white/5 text-gray-300 hover:bg-white/5">
                    <td className="p-2 whitespace-nowrap">
                      <span className="font-mono text-xs text-gray-200 bg-white/5 px-2 py-1 rounded">{click.ip || '-'}</span>
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        {countryCode && (
                          <img 
                            src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
                            alt={click.country}
                            className="w-6 h-4 object-cover rounded-sm"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span>{click.country || '-'}</span>
                      </span>
                    </td>
                    <td className="p-2 whitespace-nowrap">{click.city || '-'}</td>
                    <td className="p-2 whitespace-nowrap">
                      <span className="bg-black text-white px-2 py-0.5 rounded text-[10px] border border-purple-500">
                        {click.referrer ? (click.referrer.includes('instagram') ? 'Instagram' : 
                         click.referrer.includes('facebook') ? 'Facebook' : 
                         click.referrer.includes('twitter') || click.referrer.includes('t.co') ? 'Twitter' :
                         click.referrer.includes('tiktok') ? 'TikTok' :
                         click.referrer.includes('youtube') ? 'YouTube' : 'Direct') : 'Direct'}
                      </span>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap min-w-[120px]">
                      <div className="flex flex-col items-end">
                        <span className="text-purple-500 font-bold text-sm">
                          {click.timestamp ? new Date(click.timestamp).toLocaleString('tr-TR', { 
                            hour: '2-digit', minute: '2-digit' 
                          }) : '-'}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {click.timestamp ? new Date(click.timestamp).toLocaleString('tr-TR', { 
                            day: '2-digit', month: '2-digit', year: 'numeric'
                          }) : ''}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="5" className="text-center py-4 text-gray-400">Henüz tıklama yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ülkeler & Şehirler - LİMİTSİZ */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0a0a0a] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Ülkeler</h3>
          <div className="space-y-1 overflow-y-auto" style={{ maxHeight: 'none' }}>
            {Object.entries(data.countries || {}).map(([country, count]) => {
              const countryCode = getCountryCode(country);
              return (
                <div key={country} className="flex justify-between items-center p-2 bg-white/5 rounded text-xs">
                  <span className="text-white truncate flex items-center gap-2">
                    {countryCode && (
                      <img 
                        src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
                        alt={country}
                        className="w-6 h-4 object-cover rounded-sm"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {country}
                  </span>
                  <span className="text-purple-500 font-bold ml-2">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-[#0a0a0a] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Şehirler</h3>
          <div className="space-y-1 overflow-y-auto" style={{ maxHeight: 'none' }}>
            {Object.entries(data.cities || {}).map(([city, count]) => (
              <div key={city} className="flex justify-between items-center p-2 bg-white/5 rounded text-xs">
                <span className="text-white truncate">{city}</span>
                <span className="text-purple-500 font-bold ml-2">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cihaz Tipi & Tarayıcılar */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#0a0a0a] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Cihaz Tipi
          </h3>
          <div className="space-y-2">
            {(() => {
              // Sadece Mobile, Desktop, Tablet değerlerini filtrele
              const validPlatforms = ['Mobile', 'Desktop', 'Tablet'];
              const filteredPlatforms = Object.entries(data.platforms || {})
                .filter(([platform]) => validPlatforms.includes(platform));
              const total = filteredPlatforms.reduce((sum, [, count]) => sum + count, 0);
              
              return filteredPlatforms.length > 0 ? filteredPlatforms.map(([platform, count]) => {
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                const getDeviceIcon = (p) => {
                  if (p === 'Mobile') return '📱';
                  if (p === 'Tablet') return '📱';
                  if (p === 'Desktop') return '💻';
                  return '🖥️';
                };
                const getDeviceColor = (p) => {
                  if (p === 'Mobile') return '#22c55e';
                  if (p === 'Tablet') return '#3b82f6';
                  if (p === 'Desktop') return '#8b5cf6';
                  return '#8b5cf6';
                };
                return (
                  <div key={platform} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white flex items-center gap-2">
                        <span>{getDeviceIcon(platform)}</span>
                        {platform === 'Mobile' ? 'Mobil' : platform === 'Desktop' ? 'Masaüstü' : 'Tablet'}
                      </span>
                      <span className="text-gray-400">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-3 bg-white/5 rounded overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${Math.max(percentage, 5)}%`,
                          backgroundColor: getDeviceColor(platform)
                        }}
                      />
                    </div>
                  </div>
                );
              }) : <p className="text-gray-400 text-xs">Henüz veri yok</p>;
            })()}
          </div>
        </div>
        <div className="bg-[#0a0a0a] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            Tarayıcılar
          </h3>
          <div className="space-y-1 overflow-y-auto" style={{ maxHeight: 'none' }}>
            {Object.entries(data.browsers || {}).map(([browser, count]) => {
              const getBrowserIcon = (b) => {
                if (b.includes('Chrome')) return '🌐';
                if (b.includes('Safari')) return '🧭';
                if (b.includes('Firefox')) return '🦊';
                if (b.includes('Edge')) return '📘';
                if (b.includes('Opera')) return '🔴';
                return '🌍';
              };
              return (
                <div key={browser} className="flex justify-between items-center p-2 bg-white/5 rounded text-xs">
                  <span className="text-white truncate flex items-center gap-2">
                    <span>{getBrowserIcon(browser)}</span>
                    {browser}
                  </span>
                  <span className="text-purple-500 font-bold ml-2">{count}</span>
                </div>
              );
            })}
            {Object.keys(data.browsers || {}).length === 0 && (
              <p className="text-gray-400 text-xs">Henüz veri yok</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  // Ülke kodunu bayrak emojisine çevir
  function getFlagEmoji(countryCode) {
    if (!countryCode) return '';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }

  // Ülke adından ülke kodunu al
  function getCountryCode(countryName) {
    if (!countryName) return null;
    const countries = {
      'Turkey': 'TR', 'Türkiye': 'TR',
      'Germany': 'DE', 'Almanya': 'DE',
      'United Kingdom': 'GB', 'İngiltere': 'GB',
      'United States': 'US', 'Amerika': 'US',
      'France': 'FR', 'Fransa': 'FR',
      'Netherlands': 'NL', 'Hollanda': 'NL',
      'Denmark': 'DK', 'Danimarka': 'DK',
      'Libya': 'LY',
      'Russia': 'RU', 'Rusya': 'RU',
      'Italy': 'IT', 'İtalya': 'IT',
      'Spain': 'ES', 'İspanya': 'ES',
      'Belgium': 'BE', 'Belçika': 'BE',
      'Austria': 'AT', 'Avusturya': 'AT',
      'Switzerland': 'CH', 'İsviçre': 'CH',
      'Sweden': 'SE', 'İsveç': 'SE',
      'Norway': 'NO', 'Norveç': 'NO',
      'Finland': 'FI', 'Finlandiya': 'FI',
      'Poland': 'PL', 'Polonya': 'PL',
      'Greece': 'GR', 'Yunanistan': 'GR',
      'Portugal': 'PT', 'Portekiz': 'PT',
      'Ireland': 'IE', 'İrlanda': 'IE',
      'Czech Republic': 'CZ', 'Çekya': 'CZ',
      'Hungary': 'HU', 'Macaristan': 'HU',
      'Romania': 'RO', 'Romanya': 'RO',
      'Bulgaria': 'BG', 'Bulgaristan': 'BG',
      'Ukraine': 'UA', 'Ukrayna': 'UA',
      'Canada': 'CA', 'Kanada': 'CA',
      'Australia': 'AU', 'Avustralya': 'AU',
      'Japan': 'JP', 'Japonya': 'JP',
      'China': 'CN', 'Çin': 'CN',
      'India': 'IN', 'Hindistan': 'IN',
      'Brazil': 'BR', 'Brezilya': 'BR',
      'Mexico': 'MX', 'Meksika': 'MX',
      'Argentina': 'AR', 'Arjantin': 'AR',
      'South Korea': 'KR', 'Güney Kore': 'KR',
      'Saudi Arabia': 'SA', 'Suudi Arabistan': 'SA',
      'United Arab Emirates': 'AE', 'BAE': 'AE',
      'Egypt': 'EG', 'Mısır': 'EG',
      'Morocco': 'MA', 'Fas': 'MA',
      'South Africa': 'ZA', 'Güney Afrika': 'ZA',
      'Israel': 'IL', 'İsrail': 'IL',
      'Singapore': 'SG', 'Singapur': 'SG',
      'Malaysia': 'MY', 'Malezya': 'MY',
      'Thailand': 'TH', 'Tayland': 'TH',
      'Vietnam': 'VN',
      'Indonesia': 'ID', 'Endonezya': 'ID',
      'Philippines': 'PH', 'Filipinler': 'PH',
      'Pakistan': 'PK',
      'Bangladesh': 'BD', 'Bangladeş': 'BD',
      'Iran': 'IR', 'İran': 'IR',
      'Iraq': 'IQ', 'Irak': 'IQ',
      'Syria': 'SY', 'Suriye': 'SY',
      'Azerbaijan': 'AZ', 'Azerbaycan': 'AZ',
      'Georgia': 'GE', 'Gürcistan': 'GE',
      'Kazakhstan': 'KZ', 'Kazakistan': 'KZ',
      'Uzbekistan': 'UZ', 'Özbekistan': 'UZ'
    };
    return countries[countryName] || null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/sl-logo.png" alt="SnapLink" className="w-8 h-8 rounded-lg" />
            <div>
              <h1 className="text-base font-bold text-white">SnapLink</h1>
              <p className="text-[10px] text-gray-500">Admin Paneli</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Çıkış</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-3 py-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
          {[
            { id: 'overview', icon: BarChart3, label: 'Genel' },
            { id: 'users', icon: Users, label: 'Kullanıcılar' },
            { id: 'links', icon: LinkIcon, label: 'Linkler' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm transition-all ${
                activeTab === tab.id ? 'btn-purple' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="text-lg font-bold text-white">Genel Bakış</h2>
              <DateRangePicker onDateChange={(days) => { setSelectedDays(days); }} initialDays={selectedDays} />
            </div>
            
            {/* Linklerim Bölümü */}
            {adminLinks.length > 0 && (
              <div className="mb-6">
                <h3 className="text-base font-bold text-white mb-3">Linklerim</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {adminLinks.map((link) => (
                    <div
                      key={link.id}
                      className="bg-[#111] border border-white/10 hover:border-purple-500/50 rounded-lg p-3 transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-purple-500 font-mono text-sm font-bold">/{link.short_code}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded ${link.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                            {link.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <button
                          onClick={() => copyLink(link.short_code)}
                          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                          title="Kopyala"
                        >
                          <Copy className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                      {link.title && <div className="text-xs text-gray-400 mb-1">{link.title}</div>}
                      <div className="text-[10px] text-gray-500 truncate mb-3">{truncateUrl(link.destination_url, 35)}</div>
                      
                      {/* Aksiyon Butonları */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => window.open(`${window.location.origin}/${link.short_code}`, '_blank')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-lg text-green-500 text-xs font-medium transition-all"
                          title="Test Et"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Test Et
                        </button>
                        <button
                          onClick={() => handleViewStats(link)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-lg text-blue-500 text-xs font-medium transition-all"
                          title="İstatistikler"
                        >
                          <BarChart3 className="w-3.5 h-3.5" />
                          İstatistikler
                        </button>
                        <button
                          onClick={() => openEditLinkModal(link)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-purple-500 text-xs font-medium transition-all"
                          title="Düzenle"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Düzenle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {analytics && <StatsContent data={analytics} />}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-white">Kullanıcılar</h2>
              <button
                onClick={() => { setEditingUser(null); setNewUser({ email: '', username: '', password: '', role: 'user' }); setShowUserModal(true); }}
                className="btn-purple px-3 py-2 flex items-center gap-1 text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Yeni</span>
              </button>
            </div>

            {/* Mobil için kart görünümü */}
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="bg-[#111] border border-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-mono text-sm truncate">{user.username}</div>
                      <div className="text-gray-400 text-xs truncate">{user.email}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${user.role === 'admin' ? 'gradient-orange text-white' : 'bg-white/10 text-gray-400'}`}>
                          {user.role}
                        </span>
                        {user.is_approved ? (
                          <span className="text-green-500 text-[10px]">Onaylı</span>
                        ) : (
                          <button onClick={() => handleApproveUser(user.id)} className="text-purple-500 text-[10px]">Onayla</button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingUser(user); setNewUser({ email: user.email, username: user.username, password: '', role: user.role }); setShowUserModal(true); }} className="text-purple-500 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteUser(user.id)} className="text-red-500 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Links Tab */}
        {activeTab === 'links' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-white">Linkler</h2>
              <button
                onClick={() => { setEditingLink(null); setNewLink({ short_code: '', destination_url: '', title: '', owner_user_id: '' }); setShowLinkModal(true); }}
                className="btn-purple px-3 py-2 flex items-center gap-1 text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Yeni</span>
              </button>
            </div>

            {/* Mobil için kart görünümü */}
            <div className="space-y-3">
              {links.map((link) => (
                <div key={link.id} className="bg-[#111] border border-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-purple-500 font-mono text-sm font-bold">/{link.short_code}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${link.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {link.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      {link.title && <div className="text-gray-400 text-xs mb-1">{link.title}</div>}
                      <div className="text-gray-500 text-[10px] truncate mb-2">{truncateUrl(link.destination_url, 40)}</div>
                      {link.created_by_username && <div className="text-gray-600 text-[10px] mb-2">Kullanıcı: {link.created_by_username}</div>}
                      
                      {/* Aksiyon Butonları - Daha Görünür */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button 
                          onClick={() => window.open(`${window.location.origin}/${link.short_code}`, '_blank')} 
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-lg text-green-500 text-xs font-medium transition-all"
                          title="Test Et"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Test Et
                        </button>
                        <button 
                          onClick={() => handleViewStats(link)} 
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-lg text-blue-500 text-xs font-medium transition-all"
                          title="İstatistikler"
                        >
                          <BarChart3 className="w-3.5 h-3.5" />
                          İstatistikler
                        </button>
                        <button 
                          onClick={() => openEditLinkModal(link)} 
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-purple-500 text-xs font-medium transition-all"
                          title="Düzenle"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Düzenle
                        </button>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropdownOpenAbove(window.innerHeight - rect.bottom < 200);
                          setOpenDropdown(openDropdown === link.id ? null : link.id);
                        }}
                        className="text-gray-400 hover:text-white p-1"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      <AnimatePresence>
                        {openDropdown === link.id && (
                          <motion.div
                            initial={{ opacity: 0, y: dropdownOpenAbove ? 10 : -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: dropdownOpenAbove ? 10 : -10 }}
                            className={`absolute right-0 w-48 bg-[#1a1a1a] border border-purple-500/30 rounded-lg shadow-xl z-50 overflow-hidden ${
                              dropdownOpenAbove ? 'bottom-full mb-2' : 'top-full mt-2'
                            }`}
                          >
                            <button onClick={() => handleViewStats(link)} className="w-full text-left px-4 py-3 hover:bg-purple-500/10 flex items-center gap-3 text-white text-sm border-b border-white/5">
                              <BarChart3 className="w-4 h-4 text-blue-500" /> İstatistikler
                            </button>
                            <button onClick={() => openEditLinkModal(link)} className="w-full text-left px-4 py-3 hover:bg-purple-500/10 flex items-center gap-3 text-white text-sm border-b border-white/5">
                              <Edit className="w-4 h-4 text-purple-500" /> Düzenle
                            </button>
                            <button onClick={() => handleResetStats(link.id)} className="w-full text-left px-4 py-3 hover:bg-purple-500/10 flex items-center gap-3 text-purple-500 text-sm border-b border-white/5">
                              <RotateCcw className="w-4 h-4" /> İstatistikleri Sıfırla
                            </button>
                            <button onClick={() => handleToggleLinkStatus(link.id, link.is_active)} className="w-full text-left px-4 py-3 hover:bg-purple-500/10 flex items-center gap-3 text-white text-sm border-b border-white/5">
                              {link.is_active ? <EyeOff className="w-4 h-4 text-yellow-500" /> : <Eye className="w-4 h-4 text-green-500" />}
                              {link.is_active ? 'Devre Dışı' : 'Aktif Et'}
                            </button>
                            <button onClick={() => handleDeleteLink(link.id)} className="w-full text-left px-4 py-3 hover:bg-red-500/10 flex items-center gap-3 text-red-500 text-sm">
                              <Trash2 className="w-4 h-4" /> Sil
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User Modal - Siyah/Turuncu Tema */}
      <AnimatePresence>
        {showUserModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              className="bg-[#0a0a0a] border-2 border-purple-500/50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl shadow-purple-500/10"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/10 border-b border-purple-500/30 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h3>
                </div>
                <button onClick={() => setShowUserModal(false)} className="w-8 h-8 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Email</label>
                  <input 
                    type="email" 
                    placeholder="ornek@email.com" 
                    value={newUser.email} 
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600" 
                  />
                </div>
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Kullanıcı Adı</label>
                  <input 
                    type="text" 
                    placeholder="kullanici_adi" 
                    value={newUser.username} 
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600" 
                  />
                </div>
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Şifre</label>
                  <input 
                    type="password" 
                    placeholder={editingUser ? 'Yeni şifre (boş bırakılabilir)' : '••••••••'} 
                    value={newUser.password} 
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600" 
                  />
                </div>
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Rol</label>
                  <div className="space-y-2">
                    {/* Kullanıcı Seçeneği */}
                    <div 
                      onClick={() => setNewUser({...newUser, role: 'user'})}
                      className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                        newUser.role === 'user' 
                          ? 'bg-purple-500/20 border-purple-500' 
                          : 'bg-[#111] border-white/10 hover:border-purple-500/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${newUser.role === 'user' ? 'bg-purple-500' : 'bg-gray-700'}`}>
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className={`font-medium ${newUser.role === 'user' ? 'text-purple-500' : 'text-white'}`}>Kullanıcı</div>
                          <div className="text-xs text-gray-500">Standart kullanıcı yetkisi</div>
                        </div>
                        {newUser.role === 'user' && (
                          <div className="ml-auto">
                            <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Admin Seçeneği */}
                    <div 
                      onClick={() => setNewUser({...newUser, role: 'admin'})}
                      className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                        newUser.role === 'admin' 
                          ? 'bg-purple-500/20 border-purple-500' 
                          : 'bg-[#111] border-white/10 hover:border-purple-500/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${newUser.role === 'admin' ? 'bg-purple-500' : 'bg-gray-700'}`}>
                          <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className={`font-medium ${newUser.role === 'admin' ? 'text-purple-500' : 'text-white'}`}>Admin</div>
                          <div className="text-xs text-gray-500">Tam yönetici yetkisi</div>
                        </div>
                        {newUser.role === 'admin' && (
                          <div className="ml-auto">
                            <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={editingUser ? handleUpdateUser : handleCreateUser} 
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-orange-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 mt-2"
                >
                  {editingUser ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Link Modal - Siyah/Turuncu Tema */}
      <AnimatePresence>
        {showLinkModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              className="bg-[#0a0a0a] border-2 border-purple-500/50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl shadow-purple-500/10"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/10 border-b border-purple-500/30 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{editingLink ? 'Link Düzenle' : 'Yeni Link'}</h3>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="w-8 h-8 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Kısa Kod</label>
                  <input 
                    type="text" 
                    placeholder="ornek: promo" 
                    value={newLink.short_code} 
                    onChange={(e) => setNewLink({...newLink, short_code: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600 disabled:opacity-50" 
                    disabled={!!editingLink}
                  />
                </div>
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Hedef URL</label>
                  <input 
                    type="text" 
                    placeholder="https://ornek.com veya wa.me/..." 
                    value={newLink.destination_url} 
                    onChange={(e) => setNewLink({...newLink, destination_url: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600" 
                  />
                </div>
                <div>
                  <label className="text-xs text-purple-500 font-medium mb-2 block">Başlık (İsteğe Bağlı)</label>
                  <input 
                    type="text" 
                    placeholder="Link açıklaması" 
                    value={newLink.title} 
                    onChange={(e) => setNewLink({...newLink, title: e.target.value})} 
                    className="w-full bg-[#111] border border-purple-500/30 focus:border-purple-500 rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-gray-600" 
                  />
                </div>
                
                {allUsers.length >= 0 && (
                  <div>
                    <label className="text-xs text-purple-500 font-medium mb-2 block">Kullanıcıya Ata</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto bg-[#111] border border-purple-500/30 rounded-xl p-3">
                      {/* Admin Seçeneği - adminUserId kullanılıyor */}
                      <div 
                        onClick={() => setNewLink({...newLink, owner_user_id: adminUserId})}
                        className={`p-3 rounded-lg cursor-pointer transition-all border ${
                          (newLink.owner_user_id === adminUserId || newLink.owner_user_id === '') 
                            ? 'bg-purple-500/20 border-purple-500 text-purple-500' 
                            : 'bg-[#0a0a0a] border-white/10 text-white hover:border-purple-500/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(newLink.owner_user_id === adminUserId || newLink.owner_user_id === '') ? 'bg-purple-500' : 'bg-gray-700'}`}>
                            <Users className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">Admin (Ben)</div>
                            <div className="text-xs text-gray-500">Yönetici hesabı</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Kullanıcılar */}
                      {allUsers.map(u => (
                        <div 
                          key={u.id}
                          onClick={() => setNewLink({...newLink, owner_user_id: u.id})}
                          className={`p-3 rounded-lg cursor-pointer transition-all border ${
                            newLink.owner_user_id === u.id 
                              ? 'bg-purple-500/20 border-purple-500 text-purple-500' 
                              : 'bg-[#0a0a0a] border-white/10 text-white hover:border-purple-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${newLink.owner_user_id === u.id ? 'bg-purple-500' : 'bg-gray-700'}`}>
                              <span className="text-white text-sm font-bold">{u.username?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div>
                              <div className="font-medium text-sm">{u.username}</div>
                              <div className="text-xs text-gray-500">{u.email}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <button 
                  onClick={editingLink ? handleUpdateLink : handleCreateLink} 
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-orange-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 mt-2"
                >
                  {editingLink ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Modal */}
      <AnimatePresence>
        {showStatsModal && linkAnalytics && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 p-3 flex justify-between items-center">
                <div>
                  <h3 className="text-base font-bold text-white">Link İstatistikleri</h3>
                  <p className="text-purple-500 font-mono text-sm">/{selectedLinkForStats?.short_code}</p>
                </div>
                <button onClick={() => setShowStatsModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-3">
                {/* WhatsApp Badge - Link bazlı */}
                {selectedLinkForStats?.destination_url && (selectedLinkForStats.destination_url.includes('wa.me') || selectedLinkForStats.destination_url.includes('whatsapp.com')) && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-3">
                    <img src="/whatsapp-logo.png" alt="WhatsApp" className="w-8 h-8 rounded" />
                    <div>
                      <div className="text-green-400 font-bold text-lg">{linkAnalytics.today_whatsapp || 0} Bugün</div>
                      <div className="text-gray-400 text-xs">Toplam: {linkAnalytics.total_whatsapp || 0} WhatsApp yönlendirmesi</div>
                    </div>
                  </div>
                )}
                <StatsContent data={linkAnalytics} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Modal - Genel İstatistikler */}
      <AnimatePresence>
        {showWhatsAppModal && analytics && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#0a0a0a] border border-green-500/30 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-green-500/30 p-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <img src="/whatsapp-logo.png" alt="WhatsApp" className="w-10 h-10 rounded" />
                  <div>
                    <h3 className="text-lg font-bold text-white">WhatsApp Yönlendirmeleri</h3>
                    <p className="text-gray-400 text-xs">Başarılı yönlendirme istatistikleri</p>
                  </div>
                </div>
                <button onClick={() => setShowWhatsAppModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                {/* Bugün */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">{analytics.today_whatsapp || 0}</div>
                  <div className="text-gray-400 text-sm">Bugün (TR 00:00-00:00)</div>
                </div>
                
                {/* Toplam */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-white mb-2">{analytics.total_whatsapp || 0}</div>
                  <div className="text-gray-400 text-sm">Son 14 Gün Toplam</div>
                </div>
                
                {/* Bilgi */}
                <div className="bg-white/5 rounded-lg p-3 text-xs text-gray-400">
                  <p>📊 WhatsApp yönlendirmeleri (wa.me, whatsapp.com) otomatik olarak sayılır.</p>
                  <p className="mt-1">🕐 Her gün TR saati 00:00'da günlük sayaç sıfırlanır.</p>
                  <p className="mt-1">📅 14 günden eski veriler otomatik temizlenir.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
